import React, { useEffect, useRef, useState } from "react";
import {
  FaBug,
  FaTimes,
  FaUpload,
  FaImage,
  FaVideo,
  FaTrash,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { REPORT_BUG } from "../../Utils/apis";

export default function ReportIssue() {
  const fileInputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const user = useSelector((state) => state.user);
  const location = useLocation();

  const isAuthPage = location.pathname.startsWith("/auth");

  const userEmail = isAuthPage ? "" : user?.email || "";

  const [formData, setFormData] = useState({
    email: "",
    title: "",
    category: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState("");

  const [file, setFile] = useState(null);

  const frontendRoute = `${window.location.origin}${location.pathname}${location.search}`;

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      email: userEmail,
    }));
  }, [userEmail]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setSubmitMessage("");
  };

  const handleCategoryChange = (category) => {
    setFormData((prev) => ({
      ...prev,
      category,
    }));

    setErrors((prev) => ({
      ...prev,
      category: "",
    }));

    setSubmitMessage("");
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    const isImage = selectedFile.type.startsWith("image/");
    const isVideo = selectedFile.type.startsWith("video/");

    if (!isImage && !isVideo) {
      setErrors((prev) => ({
        ...prev,
        file: "Please upload an image or video file.",
      }));

      setFile(null);
      e.target.value = "";
      return;
    }

    setErrors((prev) => ({
      ...prev,
      file: "",
    }));

    setSubmitMessage("");
    setFile(selectedFile);
  };

  const removeFile = () => {
    setFile(null);

    setErrors((prev) => ({
      ...prev,
      file: "",
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    setFormData({
      email: userEmail,
      title: "",
      category: "",
      description: "",
    });

    setErrors({});
    setSubmitMessage("");
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;

    setIsOpen(false);
    resetForm();
  };

  const validateForm = () => {
    const newErrors = {};

    const email = formData.email.trim();
    const title = formData.title.trim();
    const description = formData.description.trim();

    if (!email) {
      newErrors.email = "Please enter your email address.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(email)) {
        newErrors.email = "Please enter a valid email address.";
      }
    }

    if (!title) {
      newErrors.title = "Please enter an issue title.";
    } else if (title.length < 3) {
      newErrors.title = "Issue title must be at least 3 characters.";
    }

    if (!formData.category) {
      newErrors.category = "Please select a category.";
    }

    if (!description) {
      newErrors.description = "Please describe the issue.";
    } else if (description.length < 10) {
      newErrors.description = "Description must be at least 10 characters.";
    }

    if (errors.file) {
      newErrors.file = errors.file;
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitMessage("");

    if (!validateForm()) {
      return;
    }

    const email = formData.email.trim();

    try {
      setIsSubmitting(true);

      const payload = new FormData();

      payload.append("email", email);
      payload.append("title", formData.title.trim());
      payload.append("category", formData.category);
      payload.append("description", formData.description.trim());
      payload.append("frontend_route", frontendRoute);

      if (file) {
        payload.append("file", file);
      }

      const response = await fetch(REPORT_BUG, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to submit issue.");
      }

      setSubmitMessage("success");

      setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (error) {
      console.error("Report issue error:", error);

      setSubmitMessage("Unable to report the issue. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Report Issue Button */}
      <button
        type="button"
        onClick={() => {
          setIsOpen(true);
          setSubmitMessage("");
        }}
        className="
    fixed
    right-0
    top-1/2
    -translate-y-1/2
    z-[9998]
    w-[30px]
    min-h-[135px]
    bg-white
    border
    border-gray-200
    rounded-l-[15px]
    shadow-[0_4px_18px_rgba(0,0,0,0.15)]
    flex
    flex-col
    items-center
    justify-center
    gap-3
    text-[#555]
    transition-all
    duration-200
    hover:w-[44px]
  "
        aria-label="Report an issue"
      >
        <FaBug size={18} className="text-[#D97745]" />

        <span
          className="
      [writing-mode:vertical-rl]
      rotate-180
      text-[12px]
      font-medium
      whitespace-nowrap
    "
        >
          Report an issue
        </span>
      </button>

      {isOpen && (
        <div
          className="
            fixed
            inset-0
            z-[99999]
            bg-black/45
            backdrop-blur-[2px]
            flex
            items-center
            justify-center
            p-4
          "
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              handleClose();
            }
          }}
        >
          <div
            className="
              w-full
              max-w-[600px]
              max-h-[92vh]
              overflow-y-auto
              bg-white
              rounded-[22px]
              shadow-[0_20px_60px_rgba(0,0,0,0.25)]
            "
          >
            {/* Header */}
            <div
              className="
                flex
                items-start
                justify-between
                px-7
                py-6
                border-b
                border-gray-100
              "
            >
              <div className="flex items-start gap-3">
                <div
                  className="
                    w-11
                    h-11
                    rounded-full
                    bg-[#FFF1E8]
                    flex
                    items-center
                    justify-center
                    shrink-0
                  "
                >
                  <FaBug size={21} className="text-[#FF5A00]" />
                </div>

                <div>
                  <h2 className="text-[21px] font-semibold text-[#171717]">
                    Report an Issue
                  </h2>

                  <p className="text-[13px] text-[#8A8A8A] mt-1">
                    Help us understand what went wrong.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="
                  w-9
                  h-9
                  rounded-full
                  flex
                  items-center
                  justify-center
                  text-gray-500
                  hover:bg-gray-100
                  hover:text-black
                  transition
                "
                aria-label="Close"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-7 py-6">
              {/* Email */}
              <div className="mb-5">
                <label className="block text-[14px] font-medium text-[#222] mb-2">
                  Email
                  <span className="text-[#FF5A00] ml-1">*</span>
                </label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  readOnly={!!userEmail}
                  placeholder="Enter your email"
                  className={`
                    w-full
                    h-[46px]
                    px-4
                    rounded-[11px]
                    border
                    outline-none
                    text-[14px]
                    ${
                      errors.email
                        ? "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
                        : userEmail
                          ? "border-[#E5E5E5] bg-[#F7F7F7] text-[#666] cursor-not-allowed"
                          : "border-[#DEDEDE] bg-white text-[#222] focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/10"
                    }
                    transition
                  `}
                />

                {errors.email && (
                  <p className="text-[11px] text-red-500 mt-1.5">
                    {errors.email}
                  </p>
                )}

                {!errors.email && userEmail && (
                  <p className="text-[11px] text-[#999] mt-1.5">
                    Email fetched from your logged-in account.
                  </p>
                )}

                {!errors.email && !userEmail && (
                  <p className="text-[11px] text-[#FF5A00] mt-1.5">
                    Please enter your email address.
                  </p>
                )}
              </div>

              {/* Issue Title */}
              <div className="mb-5">
                <label className="block text-[14px] font-medium text-[#222] mb-2">
                  Issue Title
                  <span className="text-[#FF5A00] ml-1">*</span>
                </label>

                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter a short title"
                  className={`
                    w-full
                    h-[46px]
                    px-4
                    rounded-[11px]
                    border
                    outline-none
                    text-[14px]
                    text-[#222]
                    placeholder:text-[#A5A5A5]
                    transition
                    ${
                      errors.title
                        ? "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
                        : "border-[#DEDEDE] bg-white focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/10"
                    }
                  `}
                />

                {errors.title && (
                  <p className="text-[11px] text-red-500 mt-1.5">
                    {errors.title}
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="mb-5">
                <label className="block text-[14px] font-medium text-[#222] mb-3">
                  Category
                  <span className="text-[#FF5A00] ml-1">*</span>
                </label>

                <div
                  className={`
                    grid grid-cols-2 gap-3
                    ${
                      errors.category
                        ? "rounded-[13px] ring-1 ring-red-400"
                        : ""
                    }
                  `}
                >
                  {/* BUG */}
                  <label
                    className={`
                      cursor-pointer
                      rounded-[12px]
                      border
                      px-4
                      py-3
                      flex
                      items-center
                      justify-start
                      text-left
                      gap-3
                      transition-all
                      duration-200
                      ${
                        formData.category === "bug"
                          ? "border-[#FF5A00] bg-[#FFF5EF]"
                          : "border-[#DEDEDE] bg-white hover:border-[#FF5A00]/50"
                      }
                    `}
                  >
                    <input
                        type="radio"
                        name="category"
                        value="bug"
                        checked={formData.category === "bug"}
                        onChange={() => handleCategoryChange("bug")}
                        className="shrink-0 grow-0 accent-[#FF5A00] cursor-pointer"
                        style={{
                            width: "16px",
                            minWidth: "16px",
                            maxWidth: "16px",
                            height: "16px",
                            padding: 0,
                            margin: 0,
                            flex: "0 0 16px",
                        }}
                    />

                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-[14px] font-medium text-[#222]">Bug</p>

                      <p className="text-[11px] text-[#999] mt-0.5">
                        Something is not working
                      </p>
                    </div>
                  </label>

                  {/* SUGGESTION */}
                  <label
                    className={`
                      cursor-pointer
                      rounded-[12px]
                      border
                      px-4
                      py-3
                      flex
                      items-center
                      justify-start
                      text-left
                      gap-3
                      transition-all
                      duration-200
                      ${
                        formData.category === "suggestion"
                          ? "border-[#FF5A00] bg-[#FFF5EF]"
                          : "border-[#DEDEDE] bg-white hover:border-[#FF5A00]/50"
                      }
                    `}
                  >
                    <input
                        type="radio"
                        name="category"
                        value="suggestion"
                        checked={formData.category === "suggestion"}
                        onChange={() => handleCategoryChange("suggestion")}
                        className="shrink-0 grow-0 accent-[#FF5A00] cursor-pointer"
                        style={{
                            width: "16px",
                            minWidth: "16px",
                            maxWidth: "16px",
                            height: "16px",
                            padding: 0,
                            margin: 0,
                            flex: "0 0 16px",
                        }}
                    />

                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-[14px] font-medium text-[#222]">
                        Suggestion
                      </p>

                      <p className="text-[11px] text-[#999] mt-0.5">
                        Share an idea or improvement
                      </p>
                    </div>
                  </label>
                </div>

                {errors.category && (
                  <p className="text-[11px] text-red-500 mt-1.5">
                    {errors.category}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="mb-5">
                <label className="block text-[14px] font-medium text-[#222] mb-2">
                  Description
                  <span className="text-[#FF5A00] ml-1">*</span>
                </label>

                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the issue and what happened..."
                  rows={5}
                  className={`
                    w-full
                    px-4
                    py-3
                    rounded-[11px]
                    border
                    outline-none
                    resize-none
                    text-[14px]
                    text-[#222]
                    placeholder:text-[#A5A5A5]
                    transition
                    ${
                      errors.description
                        ? "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
                        : "border-[#DEDEDE] bg-white focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/10"
                    }
                  `}
                />

                {errors.description && (
                  <p className="text-[11px] text-red-500 mt-1.5">
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Page / Route */}
              <div className="mb-5">
                <label className="block text-[14px] font-medium text-[#222] mb-2">
                  Page / Route
                </label>

                <div
                  className="
                    w-full
                    min-h-[46px]
                    px-4
                    py-3
                    rounded-[11px]
                    border
                    border-[#E5E5E5]
                    bg-[#F7F7F7]
                    text-[13px]
                    text-[#777]
                    break-all
                  "
                >
                  {frontendRoute}
                </div>

                <p className="text-[11px] text-[#999] mt-1.5">
                  This page will be automatically attached to your report.
                </p>
              </div>

              {/* File Upload */}
              <div className="mb-2">
                <label className="block text-[14px] font-medium text-[#222] mb-2">
                  Attach Screenshot / Recording
                  <span className="text-[#999] ml-1">(Optional)</span>
                </label>

                {!file ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={`
                        w-full
                        min-h-[125px]
                        rounded-[13px]
                        border-2
                        border-dashed
                        ${
                          errors.file
                            ? "border-red-400 bg-red-50/30"
                            : "border-[#DCDCDC] bg-[#FAFAFA]"
                        }
                        hover:border-[#FF5A00]
                        hover:bg-[#FFF9F5]
                        transition
                        flex
                        flex-col
                        items-center
                        justify-center
                        gap-2
                      `}
                    >
                      <div
                        className="
                          w-10
                          h-10
                          rounded-full
                          bg-[#FFF1E8]
                          flex
                          items-center
                          justify-center
                        "
                      >
                        <FaUpload size={19} className="text-[#FF5A00]" />
                      </div>

                      <span className="text-[13px] font-medium text-[#333]">
                        Upload an image or video
                      </span>

                      <span className="text-[11px] text-[#999]">
                        Screenshots or screen recordings
                      </span>
                    </button>
                  </>
                ) : (
                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-3
                      p-3
                      rounded-[12px]
                      border
                      border-[#E5E5E5]
                      bg-[#FAFAFA]
                    "
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="
                          w-10
                          h-10
                          rounded-[9px]
                          bg-[#FFF1E8]
                          flex
                          items-center
                          justify-center
                          shrink-0
                        "
                      >
                        {file.type.startsWith("video/") ? (
                          <FaVideo size={19} className="text-[#FF5A00]" />
                        ) : (
                          <FaImage size={19} className="text-[#FF5A00]" />
                        )}
                      </div>

                      <div className="min-w-0">
                        <p className="text-[13px] font-medium text-[#222] truncate">
                          {file.name}
                        </p>

                        <p className="text-[11px] text-[#999] mt-0.5">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={removeFile}
                      className="
                        w-9
                        h-9
                        rounded-full
                        flex
                        items-center
                        justify-center
                        text-gray-500
                        hover:bg-red-50
                        hover:text-red-500
                        transition
                        shrink-0
                      "
                      aria-label="Remove file"
                    >
                      <FaTrash size={17} />
                    </button>
                  </div>
                )}

                {errors.file && (
                  <p className="text-[11px] text-red-500 mt-1.5">
                    {errors.file}
                  </p>
                )}

                <p className="text-[11px] text-[#999] mt-2">
                  You can attach a screenshot or screen recording showing the
                  issue.
                </p>
              </div>

              {/* Submit Message */}
              {submitMessage && (
                <div
                  className={`
                    mt-5
                    px-4
                    py-3
                    rounded-[10px]
                    text-[13px]
                    ${
                      submitMessage === "success"
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-600 border border-red-200"
                    }
                  `}
                >
                  {submitMessage === "success"
                    ? "Issue reported successfully."
                    : submitMessage}
                </div>
              )}

              {/* Footer */}
              <div
                className="
                  flex
                  justify-end
                  gap-3
                  mt-7
                  pt-5
                  border-t
                  border-gray-100
                "
              >
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="
                    px-5
                    h-[44px]
                    rounded-full
                    border
                    border-[#D8D8D8]
                    text-[13px]
                    font-medium
                    text-[#333]
                    hover:bg-[#F7F7F7]
                    transition
                  "
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="
                    min-w-[125px]
                    h-[44px]
                    px-6
                    rounded-full
                    bg-[#FF5A00]
                    text-white
                    text-[13px]
                    font-medium
                    hover:bg-[#E95100]
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                    transition
                    flex
                    items-center
                    justify-center
                  "
                >
                  {isSubmitting ? "Submitting..." : "Submit Issue"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}